import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import StagePicker from "./StagePicker";
import { useNavigate, useLocation } from "react-router-dom";
import { useApiClient } from "../../service/API";
import FileUpload from "../FileHandling/FileUpload";
import FileList from "../FileHandling/FileList";
import FileViewer from "../FileHandling/FileViewer";
import NotificationModal from "./NotificationModal";
import ConfirmationModal from "./ConfirmationModal";
import FileUploadingModal from "./FileUploadingModal";
import TextInputModal from "./TextInputModal";
import EvidenceViewerModal from "./EvidenceViewerModal";
import SearchableDropdown from "./SearchableDropdown";
import UrgentRequestModal from "./UrgentRequestModal";
import UrgentDetailsModal from "./UrgentDetailsModal";
import Modal from "./Modal";
import { useAuth } from "../../auth/AuthHandler";
import { useServiceTypes, useDefaultStages } from "../../hooks/useTicketStages";
import DateInputModal from "./DateInputModal";
import { faPen } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ErrorReworkModal from "./ErrorReworkModal";
import TicketAddBackupModal from "./Discussion/TicketAddBackupModal";
import NumberAndAttachmentModal from "./NumberAndAttachmentModal";
import { listCompanyDomains } from "../CompanyDomain/companyDomainApi";
import { getApiErrorMessage } from "../../service/errorHandler";
import "../../styling/TicketForm.css";

function getDocsByType(docs, type, includeAll = false) {
  const list = Array.isArray(docs) ? docs.filter(Boolean) : [];
  return (includeAll ? list : list.filter(doc => doc.type === type))
    .map(doc => ({ name: doc.name, fileId: doc.fileId, type: doc.type }));
}

function fileSelectionKey(file) {
  if (!file) return "";
  return JSON.stringify([file.type ?? "FILE", file.fileId ?? file.name ?? ""]);
}

function TicketForm({
  title,
  ticketInfo: incomingTicketInfo,
  ticketId,
  onTicketPatched = () => {},
  onSidePanelsInvalidated = () => {},

  // ✅ urgents from container/hook
  urgentList = [],
  loadingUrgents = false,
  pendingUrgent = null,
  activeUrgent = null,
  urgentLock = false,
  onRefreshUrgents = () => {},
}) {
  const { userRoles, userEmail } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const apiClient = useApiClient();
  const apiRef = useRef(apiClient);
  useEffect(() => { apiRef.current = apiClient; }, [apiClient]);

  // --- treat create mode specially so we never seed from old ticketInfo ---
  const isCreate = title === "create-ticket";
  const ticketInfo = !isCreate && incomingTicketInfo?.id != null &&
    (ticketId == null || String(incomingTicketInfo.id) === String(ticketId))
      ? incomingTicketInfo
      : null;

  // --- Local state (initializers depend on mode) ---
  const [currentTicketId, setCurrentTicketId] = useState(
    isCreate ? null : (ticketId || ticketInfo?.id || null)
  );

  const [remark, setRemark] = useState(isCreate ? null : (ticketInfo?.remark ?? null));
  const [quotation, setQuotation] = useState(isCreate ? null : (ticketInfo?.quotation ?? null));
  const [ticketStatus, setTicketStatus] = useState(isCreate ? null : (ticketInfo?.status ?? null));
  const [ticketStage, setTicketStage] = useState(isCreate ? "" : (ticketInfo?.stage ?? null));
  const [sysFreightRef, setSysFreightRef] = useState(isCreate ? null : (ticketInfo?.sysFreightRef ?? null));
  const [bookingRef, setBookingRef] = useState(isCreate ? null : (ticketInfo?.bookingRef ?? null));
  const [assignTo, setAssignTo] = useState(isCreate ? null : (ticketInfo?.assignedTo ?? null));
  const [serviceType, setServiceType] = useState(isCreate ? "" : (ticketInfo?.serviceType ?? ""));
  const [ticketFrom, setTicketFrom] = useState(isCreate ? null : (ticketInfo?.createdBy ?? null));
  const [sobAtaDate, setSOBATADate] = useState(isCreate ? null : (ticketInfo?.sobatadate ?? ticketInfo?.SOBATADate ?? null));
  const [etdEtaDate, setETDETADate] = useState(isCreate ? null : (ticketInfo?.etdetadate ?? ticketInfo?.ETDETADate ?? null));
  const [deliveryAgent, setDeliveryAgent] = useState(isCreate ? null : (ticketInfo?.deliveryAgent ?? null));
  const [showSysRefModal, setShowSysRefModal] = useState(false);
  const [showFileNumberModal, setShowFileNumberModal] = useState(false);
  const [sysRefInput, setSysRefInput] = useState(isCreate ? "" : (ticketInfo?.sysFreightRef || ""));
  const [jobFileNumberInput, setjobFileNumberInput] = useState(isCreate ? "" : (ticketInfo?.jobFileNumber || ""));
  const [boBackupEmail, setBoBackupEmail] = useState(isCreate ? null : (ticketInfo?.boBackUpEmail ?? null));
  const [ticketCompletedDate, setTicketCompletedDate] = useState(
    isCreate ? null : (ticketInfo?.ticketCompletedDate ?? null)
  );
  const [showEvidenceViewer, setShowEvidenceViewer] = useState(false);
  const [selectedEvidenceFile, setSelectedEvidenceFile] = useState(null);

  const [selectedStageName, setSelectedStageNames] = useState(
    isCreate ? [] : (Array.isArray(ticketInfo?.selectedStageName) ? ticketInfo.selectedStageName : [])
  );

  const [files, setFiles] = useState(isCreate ? [] : getDocsByType(ticketInfo?.docs, "TICKET"));
  const [quotationFile, setQuotationFile] = useState(isCreate ? [] : getDocsByType(ticketInfo?.docs, "QUOTATION"));
  const [evidenceFiles, setEvidenceFiles] = useState( isCreate ? [] : getDocsByType(ticketInfo?.docs, "FILE_EVIDENCE"));
  const [userList, setUserList] = useState([]);
  const [ticketFolderName, setTicketFolderName] = useState(isCreate ? null : (ticketInfo?.folderName || null));
  const [selectedFile, setSelectedFile] = useState(isCreate ? null : (
    [...getDocsByType(ticketInfo?.docs, "TICKET"), ...getDocsByType(ticketInfo?.docs, "QUOTATION")][0] ?? null
  ));

  const [errors, setErrors] = useState({
    remark: "",
    SysFreight: "",
    serviceType: "",
    ticketStage: "",
    etdEtaDate: "",
    quotation: "",
    assignTo: "",
    deliveryAgent: "",
    assignTo: ""
  });

  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");
  const [showUpdateConfirmation, setShowUpdateConfirmation] = useState(false);
  const [showCompleteConfirmation, setShowCompleteConfirmation] = useState(false);
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  const [showDoneConfirmation, setShowDoneConfirmation] = useState(false);
  const [fileUploading, setFileUploading] = useState(false);
  const [onCreating, setOnCreating] = useState(false);

  const [showRemoveBackupUserModal, setShowRemoveBackupUserModal] = useState();
  const [isRemarkModalOpen, setIsRemarkModalOpen] = useState(false);
  const [isQuotationModalOpen, setIsQuotationModalOpen] = useState(false);
  const [showInputPopup, setShowInputPopup] = useState(false);
  const [dateInputTitle, setDateInputTitle] = useState([]);
  const [editSOBATADATE, setEditSOBATADATE] = useState(true);
  const [editAssignTo, setEditAssignTo] = useState(true);
  const [editSysFreight, setEditSysFreight] = useState(true);
  const [isChecked, setIsChecked] = useState(false);
  const [onlyDate, setOnlyDate] = useState(false);

  const { services } = useServiceTypes();
  const { stages: defaultStages } = useDefaultStages(serviceType);

  const [showModal, setShowModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isModalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalLabels, setModalLabels] = useState("");
  const [actionType, setActionType] = useState("");
  const [completeMessage, setCompeleteMessage] = useState("");

  const [showUrgentModal, setShowUrgentModal] = useState(false);
  const [showUrgentDetails, setShowUrgentDetails] = useState(false);
  const [showUrgentCompleteConfirmation, setShowUrgentCompleteConfirmation] = useState(false);

  const [backupButon, setBackupButton] = useState(false);

  const [companyDomainMap, setCompanyDomainMap] = useState({});

  // --- keep local hydrator, but also inform parent hook via onTicketPatched ---
  const applyTicketDataLocal = useCallback((t) => {
    if (!t) return;
    if (!isCreate && ticketId != null && String(t.id) !== String(ticketId)) return;
    setRemark(t.remark ?? null);
    setTicketStatus(t.status ?? null);
    setTicketStage(t.stage ?? null);
    setSysFreightRef(t.sysFreightRef ?? null);
    setAssignTo(t.assignedTo ?? null);
    setServiceType(t.serviceType ?? "");
    setTicketFrom(t.createdBy ?? null);
    setETDETADate(t.etdetadate ?? t.ETDETADate ?? null);
    setSOBATADate(t.sobatadate ?? t.SOBATADate ?? null);
    setQuotation(t.quotation ?? null);
    setDeliveryAgent(t.deliveryAgent ?? null);
    setCurrentTicketId(t.id ?? null);
    setTicketFolderName(t.folderName ?? null);
    setTicketCompletedDate(t.ticketCompletedDate ?? null);
    setBookingRef(t.bookingRef ?? null);
    setBoBackupEmail(t.boBackUpEmail ?? null); 
    setjobFileNumberInput(t.jobFileNumber ?? null);

    const allDocs = Array.isArray(t.docs) ? t.docs : [];
    const ticketDocs    = getDocsByType(allDocs, "TICKET");
    const quotationDocs = getDocsByType(allDocs, "QUOTATION");
    const evidenceDocs = getDocsByType(allDocs, "FILE_EVIDENCE");
    setEvidenceFiles(evidenceDocs);
    setSelectedEvidenceFile(evidenceDocs.length ? evidenceDocs[0] : null)
    setFiles(ticketDocs);
    setQuotationFile(quotationDocs);
    const merged = [...ticketDocs, ...quotationDocs];
    setSelectedFile(merged.length ? merged[0] : null);

    if (Array.isArray(t.selectedStageName)) {
      setSelectedStageNames(t.selectedStageName);
    } else {
      setSelectedStageNames([]);
    }
  }, [isCreate, ticketId]);

  useEffect(() => {
    let mounted = true;

    const loadCompanyDomains = async () => {
      try {
        const domains = await listCompanyDomains(apiRef.current);

        // Build map: { "kaselog.com.sg": "KASE LOG", ... }
        const map = {};
        (domains || [])
          .filter(d => d?.status === "ACTIVE" && !d?.retiredAt)
          .forEach(d => {
            if (d?.name) map[d.name.toLowerCase()] = d.company || d.name;
          });

        if (mounted) setCompanyDomainMap(map);
      } catch (err) {
        // silent fallback (will show raw domain if API fails)
        // console.error("Failed to load company domains", err);
      }
    };

    loadCompanyDomains();
    return () => { mounted = false; };
  }, []);

  // One hydrator: replace files from the matching ticket, never append them.
  useEffect(() => {
    if (isCreate) return;
    if (ticketInfo) {
      applyTicketDataLocal(ticketInfo);
      return;
    }

    // No matching snapshot yet: clear attachments and both preview selections.
    setCurrentTicketId(ticketId ?? null);
    setFiles([]);
    setQuotationFile([]);
    setEvidenceFiles([]);
    setSelectedFile(null);
    setSelectedEvidenceFile(null);
    setTicketFolderName(null);
    setShowEvidenceViewer(false);
  }, [isCreate, ticketInfo, ticketId, applyTicketDataLocal]);

  // roles / users
  useEffect(() => {
    const fetchUserList = async () => {
      try {
        const userListGet = await apiRef.current.get(`/users/bouser-list`);
        setUserList(userListGet.data.filter(u => u.email !== assignTo));
      } catch { /* silent */ }
    };
    if (["BOMANAGER", "FOMANAGER", "ADMIN", "BOUSER"].some(r => userRoles.includes(r))) {
      fetchUserList();
    }
  }, [userRoles]);

  const nameByCode = useMemo(() => {
    const m = new Map();
    (defaultStages || []).forEach(s => m.set(s.code, s.name));
    return m;
  }, [defaultStages]);

  const codeByName = useMemo(() => {
    const m = new Map();
    (defaultStages || []).forEach(s => m.set(s.name, s.code));
    return m;
  }, [defaultStages]);

  const defaultFlowNames = useMemo(
    () => (defaultStages || [])
      .slice()
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
      .map(s => s.name),
    [defaultStages]
  );

  const stages = useMemo(() => {
    if (!defaultFlowNames?.length) return [];

    let base = [...defaultFlowNames];

    // optional filter by selectedStageName (keep this part as you intended)
    if (selectedStageName?.length) {
      const wantedNames = (selectedStageName || [])
        .map(v => nameByCode.get(v) || (codeByName.has(v) ? v : null))
        .filter(Boolean);
      const wanted = new Set(wantedNames);
      base = defaultFlowNames.filter(n => wanted.has(n));
    }

    // Booking Creation: insert whichever exists in default flow (string, not array)
    const bookingStage =
      defaultFlowNames.includes("Booking Creation In Sysfreight")
        ? "Booking Creation In Sysfreight"
        : (defaultFlowNames.includes("Booking Creation") ? "Booking Creation" : null);

    if (bookingStage && !base.includes(bookingStage)) {
      const pos = defaultFlowNames.indexOf(bookingStage);
      base.splice(Math.min(pos, base.length), 0, bookingStage);
    }

    if (defaultFlowNames.includes("Ticket Closed") && !base.includes("Ticket Closed")) {
      base.push("Ticket Closed");
    }

    return Array.from(new Set(base));
  }, [defaultFlowNames, selectedStageName, nameByCode, codeByName]);

  const currentStageIndex = useMemo(
    () => (stages || []).findIndex(s => s === ticketStage),
    [stages, ticketStage]
  );
  const ETAETDStageIndex = useMemo(
    () => (stages || []).findIndex(s => ["ETD Port Of Loading", "ETA Port Of Arrival"].includes(s)),
    [stages]
  );
  const SOBATAStageIndex = useMemo(
    () => (stages || []).findIndex(s => ["Final SOB Confirmation", "Final ATA Confirmation"].includes(s)),
    [stages]
  );

  const resetUiForTicket = useCallback(() => {
    setIsRemarkModalOpen(false);
    setIsQuotationModalOpen(false);
    setShowUpdateConfirmation(false);
    setShowCompleteConfirmation(false);
    setShowCancelConfirmation(false);
    setShowDoneConfirmation(false);
    setShowInputPopup(false);
    setModalOpen(false);
    setShowModal(false);
    setOnCreating(false);
    setEditSOBATADATE(true);
    setEditAssignTo(true);
    setEditSysFreight(true);
    setOnlyDate(false);
    setDateInputTitle([]);
    setErrors({
      remark: "",
      SysFreight: "",
      serviceType: "",
      ticketStage: "",
      etdEtaDate: "",
      quotation: "",
      assignTo: ""
    });
  }, []);

  // Clears all user-entered values when starting a brand-new create flow
  const clearCreateForm = useCallback(() => {
    // IDs / selection
    setCurrentTicketId(null);
    setSelectedStageNames([]);
    setServiceType("");
    setTicketStage("");
    setAssignTo(null);
    setDeliveryAgent(null);

    // Text areas / fields
    setRemark(null);
    setQuotation(null);
    setSysFreightRef(null);
    setBookingRef(null);

    // Dates
    setETDETADate(null);
    setSOBATADate(null);

    // Files
    setFiles([]);
    setQuotationFile([]);
    setEvidenceFiles([]);
    setSelectedFile(null);
    setSelectedEvidenceFile(null);
    setShowEvidenceViewer(false);
    setTicketFolderName(null);

    // UI flags
    setIsChecked(false);
    setOnlyDate(false);
    setShowInputPopup(false);
    setDateInputTitle([]);
    setEditSOBATADATE(true);
    setEditAssignTo(true);
    setEditSysFreight(true);
    setShowUpdateConfirmation(false);
    setShowCompleteConfirmation(false);
    setShowCancelConfirmation(false);
    setShowDoneConfirmation(false);
    setModalOpen(false);
    setShowModal(false);
    setOnCreating(false);

    // Errors
    setErrors({
      remark: "",
      SysFreight: "",
      serviceType: "",
      ticketStage: "",
      etdEtaDate: "",
      quotation: "",
      assignTo: "",
      deliveryAgent: ""
    });
  }, []);

  // When creating, a change to ?key=… starts a fresh form.
  // Same key -> keep whatever is currently in state.
  // put these near your other refs
  const lastTitleRef = useRef(title);
  const lastCreateKeyRef = useRef(null);

  // single source of truth for the "create" key in the URL
  const createKey = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("key") ?? "__NO_KEY__";
  }, [location.search]);

  useEffect(() => {
    const prevTitle = lastTitleRef.current;
    const enteredCreateMode = prevTitle !== "create-ticket" && title === "create-ticket";
    const keyChangedWhileCreating =
      title === "create-ticket" && lastCreateKeyRef.current !== createKey;

    // If we just entered create mode OR the key changed while in create mode,
    // blow away *all* carry-over state from the last viewed ticket.
    if (enteredCreateMode || keyChangedWhileCreating) {
      clearCreateForm();                 // <-- your existing function that resets everything
    }

    // track for the next pass
    lastTitleRef.current = title;
    lastCreateKeyRef.current = title === "create-ticket" ? createKey : null;
  }, [title, createKey, clearCreateForm]);

  const handleToggleChange = () => setIsChecked(prev => !prev);

  const handleExpandClick = () => setIsRemarkModalOpen(true);
  const handleQuotationExpandClick = () => setIsQuotationModalOpen(true);
  const closeRemarkModal = () => setIsRemarkModalOpen(false);
  const closeQuotationModal = () => setIsQuotationModalOpen(false);

  const handleUpdateButton = useCallback((id) => {
    navigate(`/update-ticket/${id}`);
  }, [navigate]);

  const handleApiError = (error, defaultMessage) => {
    const message = getApiErrorMessage(error, defaultMessage);

    setShowUpdateConfirmation(false);
    setShowCompleteConfirmation(false);
    setShowNotification(true);
    setNotificationMessage("[ERROR] " + message);
  };

  const handleDeliveryAgent = (e) => {
    setDeliveryAgent(e);
    setErrors(prev => ({ ...prev, deliveryAgent: "" }));
  };

  const handleTextAreaChange = (e) => {
    setRemark(e.target.value);
    setErrors(prev => ({ ...prev, remark: "" }));
  };

  const handleQuotationAreaChange = (e) => {
    setQuotation(e.target.value);
    setErrors(prev => ({ ...prev, quotation: "" }));
  };

  const removeFile = (name) => {
    setFiles(prev => prev.filter(file => file.name !== name));
    setNotificationMessage("File deleted successfully");
    setShowNotification(true);
  };

  const removeQuotationFile = (name) => {
    setQuotationFile(prev => prev.filter(file => file.name !== name));
    setNotificationMessage("File deleted successfully");
    setShowNotification(true);
  };

  const nextStageFrom = useCallback((current) => {
    const idx = stages.findIndex(s => s === current);
    return idx === -1 ? (stages[0] ?? null) : (stages[idx + 1] ?? null);
  }, [stages]);

  const handleAddSOBDate = () => {
    const etaEtdLabel = ETAETDStageIndex !== -1 ? stages[ETAETDStageIndex] : null;
    const sobAtaLabel = SOBATAStageIndex !== -1 ? stages[SOBATAStageIndex] : null;
    let label = null;
    if (currentStageIndex !== -1 && SOBATAStageIndex !== -1 && currentStageIndex >= SOBATAStageIndex) {
      label = sobAtaLabel;
    } else {
      label = etaEtdLabel;
    }
    if (!label) return;
    setDateInputTitle([label]);
    setShowInputPopup(true);
    setOnlyDate(true);
  };

  useEffect(() => {
    let newDateInputTitles = [];
    if (
      title === "view-ticket" &&
      ["BOUSER", "ADMIN"].some(role => userRoles.includes(role)) &&
      ((currentStageIndex < SOBATAStageIndex) && (currentStageIndex > ETAETDStageIndex) && !etdEtaDate) &&
      (ticketStage !== "Ticket Closed" && ticketStatus !== "COMPLETED")
    ) {
      const label = stages[ETAETDStageIndex];
      if (label) {
        newDateInputTitles.push(label);
        setOnlyDate(true);
      }
    }
    setDateInputTitle(prev => [...prev, ...newDateInputTitles.filter(v => !prev.includes(v))]);
  }, [title, ticketStage, ticketStatus, stages, ETAETDStageIndex, SOBATAStageIndex, currentStageIndex, etdEtaDate, userRoles]);

  useEffect(() => {
    setShowInputPopup((dateInputTitle?.length || 0) > 0);
  }, [dateInputTitle]);

  const handleUserInputSubmit = async (inputValue) => {
    try {
      const current = ticketStage || (stages[0] || null);
      const nextStageValue = nextStageFrom(current);

      const jsonPayload = {
        sysFreightRef,
        bookingRef,
        stage: ticketStage || stages[1],
        nextStage: nextStageValue,
        SOBATADate: sobAtaDate,
        ETDETADate: etdEtaDate,
        onlyDate
      };

      if (dateInputTitle.some(t => ["ETD Port Of Loading", "ETA Port Of Arrival"].includes(t))) {
        jsonPayload.ETDETADate = inputValue[dateInputTitle[0]];
      }
      if (dateInputTitle.some(t => ["Final SOB Confirmation", "Final ATA Confirmation"].includes(t))) {
        jsonPayload.SOBATADate = inputValue[dateInputTitle[0]];
      }

      const response = await apiRef.current.put(`/tickets/update-shipdate/${currentTicketId}`, jsonPayload, {
        headers: { "Content-Type": "application/json" },
      });

      if (response.status === 200) {
        resetUiForTicket();
        applyTicketDataLocal(response.data);
        onTicketPatched?.(response.data);
        setDateInputTitle([]);
        setShowInputPopup(false);
        setNotificationMessage(dateInputTitle[0] + " Date submitted successfully");
        setShowNotification(true);
        setOnlyDate(false);
      }
    } catch (error) {
      handleApiError(error, "Error submitting ticket. Please try again.");
    }
  };

  const validateFields = () => {
    const newErrors = {};
    let isValid = true;

    if (title === "view-ticket" && !editSOBATADATE) {
      if (currentStageIndex <= SOBATAStageIndex || SOBATAStageIndex === -1) {
        if(!etdEtaDate){
          newErrors.etdEtaDate = "Date is required.";
          isValid = false;
        }
      } else if (currentStageIndex > SOBATAStageIndex && SOBATAStageIndex !== -1) {
        if(!sobAtaDate){
          newErrors.etdEtaDate = "Date is required.";
          isValid = false;
        }
      }
    } else if (title === "view-ticket" && !editAssignTo) {
      if (!assignTo) {
        newErrors.assignTo = "Assign to is required.";
        isValid = false;
      }
    } else if (title === "view-ticket" && !editSysFreight) {
      if (!sysFreightRef?.trim()) {
        newErrors.SysFreight = "Sysfreight Reference Number is required.";
        isValid = false;
      }
    } else {
      if (!sysFreightRef?.trim() && !bookingRef?.trim()) {
        newErrors.SysFreight = "Either BookingRef or SysFreightRef must be provided.";
        newErrors.bookingRef = "Either BookingRef or SysFreightRef must be provided.";
        isValid = false;
      }
      if (!remark) {
        newErrors.remark = "Remark is required.";
        isValid = false;
      }
      if (!serviceType) {
        newErrors.serviceType = "Service Type is required.";
        isValid = false;
      }
      if (title !== "create-ticket" && ticketStatus !== "NEW" && ticketInfo?.assignedTo && !assignTo?.trim()) {
        newErrors.assignTo = "Please select a person to assign the ticket to.";
        isValid = false;
      }
      if (
        title === "view-ticket" &&
        !etdEtaDate &&
        (currentStageIndex >= SOBATAStageIndex) &&
        stages.some(s => ["ETD Port Of Loading", "ETA Port Of Arrival"].includes(s))
      ) {
        newErrors.etdEtaDate = "A date is required for this stage.";
        isValid = false;
      }
      if (selectedStageName.includes("HBL Creation") && !deliveryAgent) {
        newErrors.deliveryAgent = "Delivery agent is required.";
        isValid = false;
      }
    }

    setErrors(newErrors);
    if (!isValid) {
      setNotificationMessage("Please fill up all the necessary column!");
      setShowNotification(true);
    }
    return isValid;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateFields()) return;

    const jsonPayload = {
      remark,
      docs: [...(files || []), ...(quotationFile || [])].map(file => ({
        fileId: file.fileId,
        name: file.name
      })) || [],
      folderName: ticketFolderName,
      assignToEmail: assignTo,
      sysFreightRef,
      bookingRef,
      serviceType,
      deliveryAgent,
      quotation,
      selectedStageName,
      stage: ticketStage || (stages[0] || ""),
      nextStage: nextStageFrom(ticketStage || stages[0]),
    };

    try {
      if (title === "create-ticket") {
        setOnCreating(true);
        const response = await apiRef.current.post("/tickets/create-ticket", jsonPayload, {
          headers: { "Content-Type": "application/json" },
        });
        if (response.status === 200) {
          resetUiForTicket();
          applyTicketDataLocal(response.data);
          onTicketPatched?.(response.data);
          setNotificationMessage("Ticket created successfully!");
          setShowNotification(true);
          navigate(`/view-ticket/${response.data.id}`, {
            state: { fromCreate: true, ticketData: response.data },
            replace: true
          });
        }
      }
    } catch (error) {
      if (
        error?.businessExceptionDescription === "SysFreightRef already exists" ||
        error?.message?.includes("SysFreight Reference Number already exists")
      ) {
        setErrors(prev => ({ ...prev, SysFreight: "Sysfreight Reference Number is duplicated." }));
      }
      handleApiError(error, "Error creating ticket. Please contact IT support.");
    } finally {
      setOnCreating(false);
    }
  };

  const handleUpdateTrigger = (event) => {
    event.preventDefault();
    if (!validateFields()) return;

    if (
      (title === "update-ticket" && currentTicketId) ||
      (title === "view-ticket" && !editSOBATADATE) ||
      (title === "view-ticket" && !editAssignTo) ||
      (title === "view-ticket" && !editSysFreight)
    ) {
      setShowUpdateConfirmation(true);
    }
  };

  const handleCancelButton = (e) => {
    e.preventDefault();
    if (title === "view-ticket") navigate("/");
    else if (title === "create-ticket") navigate(-1);
    else navigate(`/view-ticket/${currentTicketId}`);
  };

  const handleBackToHome = (e) => {
    e.preventDefault();
    navigate("/");
  };

  const handleModalClose = () => {
    setShowNotification(false);
    if (title === "update-ticket" && notificationMessage === "Ticket updated successfully!") {
      navigate(`/view-ticket/${currentTicketId}`);
    }
  };

  const fetchFile = async (name, fileId) => {
    try {
      const response = await apiRef.current.get("/files/retrieve", {
        params: { fileId },
        responseType: "arraybuffer",
        headers: { "Accept": "application/json" }
      });
      if (response.status === 200) {
        const blob = new Blob([response.data]);
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", name);
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
      } else {
        throw new Error(`Server returned status: ${response.status}`);
      }
    } catch (err) {
      handleApiError(err, "Unable to fetch the file.");
    }
  };

  const downloadAllFiles = async () => {
    const merged = [...(files || []), ...(quotationFile || [])];
    for (const file of merged) {
      await fetchFile(file.name, file.fileId);
    }
  };

  const handleUpdate = async (event) => {
    event.preventDefault();
    if (!validateFields()) return;

    const jsonPayload = {
      remark,
      docs: [...(files || []), ...(quotationFile || [])].map(file => ({
        fileId: file.fileId,
        name: file.name,
        type: file.type
      })) || [],
      folderName: ticketFolderName,
      assignToEmail: assignTo,
      sysFreightRef,
      bookingRef,
      serviceType,
      SOBATADate: sobAtaDate,
      ETDETADate: etdEtaDate,
      quotation,
      onlyDate,
      deliveryAgent,
      selectedStageName,
      stage: ticketStage || (stages[1] || stages[0] || ""),
      nextStage: nextStageFrom(ticketStage || stages[1] || stages[0]),
    };

    try {
      const response = await apiRef.current.put(`/tickets/update-ticket/${currentTicketId}`, jsonPayload, {
        headers: { "Content-Type": "application/json" },
      });
      if (response.status === 200) {
        resetUiForTicket();
        applyTicketDataLocal(response.data);
        onTicketPatched?.(response.data);
        setNotificationMessage("Ticket updated successfully!");
        setShowUpdateConfirmation(false);
        setShowNotification(true);
        setEditSOBATADATE(true);
        setEditAssignTo(true);
        setEditSysFreight(true);
      }
    } catch (error) {
      console.error(error);
      if (error?.response?.data?.businessExceptionDescription === "SysFreightRef already exists") {
        setErrors(prev => ({ ...prev, SysFreight: "Sysfreight Reference Number is duplicated." }));
      }
      handleApiError(error, "Error updating data. Please contact IT support.");
    }
  };

  const handleSubmitSysRef = async (inputValue) => {
    try {
      const current = ticketStage || (stages[0] || null);
      const nextStageValue = nextStageFrom(current);
      const url = `/tickets/complete-ticket/${currentTicketId}?nextStage=${encodeURIComponent(nextStageValue ?? "")}&sysFreightRef=${encodeURIComponent(inputValue)}`;

      const response = await apiRef.current.patch(url);
      if (response.status === 200) {
        resetUiForTicket();
        applyTicketDataLocal(response.data);
        onTicketPatched?.(response.data);
        setNotificationMessage(`Ticket ${currentTicketId}: SysFreight Ref saved and "${current}" completed.`);
        setShowNotification(true);
        await onRefreshUrgents();
      } else {
        handleApiError(null, "Error completing ticket. Please contact IT support.");
      }
    } catch (error) {
      handleApiError(error, "Error completing ticket. Please contact IT support.");
    } finally {
      setShowSysRefModal(false);
    }
  };

  const handleComplete = async (statusParse) => {
    setEditSOBATADATE(true);
    setEditAssignTo(true);
    setEditSysFreight(true);
    if (ticketStage.includes("Booking") && ticketStatus === "NEW") {
      setSysRefInput(sysFreightRef || "");
      setShowSysRefModal(true);
      return;
    }
    if (["ETD Port Of Loading", "ETA Port Of Arrival", "Final SOB Confirmation", "Final ATA Confirmation"].includes(ticketStage)) {
      if (ticketStatus === "IN PROGRESS") setOnlyDate(false);
      setDateInputTitle([ticketStage]);
      setShowInputPopup(true);
    } else {
      if(statusParse === "COMPLETE"){
        if((ticketStage === "HBL Creation" && ticketStatus === "WAITING FOR SI")){
          statusParse = "IN PROGRESS";
        }
      }
      setCompeleteMessage(`Are you sure you want to mark stage ${ticketStage} as ${statusParse}.`);
      setShowCompleteConfirmation(true);
    }
  };

  useEffect(() => {
    if(title === "create-ticket"){
      setSelectedStageNames([]); // default-all for new service
    }
  }, [serviceType]);

  const handleDone = async () => setShowDoneConfirmation(true);

  const handleCloseModal = () => {
    setShowModal(false);
    setErrorMessage("");
  };

  const confirmComplete = async (e) => {
    e.preventDefault();
    try {
      if (showCompleteConfirmation) {
        const current = ticketStage || (stages[0] || null);
        const nextStageValue = nextStageFrom(current);
        const response = await apiRef.current.patch(
          `/tickets/complete-ticket/${currentTicketId}?nextStage=${encodeURIComponent(nextStageValue ?? "")}`
        );
        if (response.status === 200) {
          resetUiForTicket();
          applyTicketDataLocal(response.data);
          onTicketPatched?.(response.data);
          await onRefreshUrgents();
        }
        setNotificationMessage(`Ticket ${currentTicketId} have been updated with ${response.data.stage} and ${response.data.status}!`);
      } else {
        handleApiError(null, "Error completing ticket. Please contact IT support.");
      }
    } catch (error) {
      handleApiError(error, "Error completing ticket. Please contact IT support.");
    } finally {
      setShowCompleteConfirmation(false);
      setShowNotification(true);
    }
  };

  const handleDateModalOnClose = (e) => {
    e?.preventDefault?.();
    setDateInputTitle([]);
    setShowInputPopup(false);
  };

  const handleSelectionChange = (selectedEmail) => {
    setAssignTo(selectedEmail);
    setErrors(prev => ({ ...prev, assignTo: "" }));
  };

  const handleRemoveBackupUser = async (e) => {
    e.preventDefault();
    try{
      const response = await apiRef.current.patch(`tickets/ticket/remove-backup/${currentTicketId}`);
      if (response.status === 200) {
        setNotificationMessage(`Ticket ${currentTicketId} have successful remove backup user!`);
        applyTicketDataLocal(response.data);
        onTicketPatched?.(response.data);
      }
    } catch (error) {
      console.error("Error during approval:", error?.response?.data || error?.message);
    } finally {
      setShowRemoveBackupUserModal(false);
      setShowNotification(true);
    }
  }

  const handleDoneUpdate = async (e) => {
    e.preventDefault();
    try {
      const response = await apiRef.current.patch(`/tickets/DONE/${currentTicketId}`);
      if (response.status === 200) {
        setNotificationMessage(`Ticket ${currentTicketId} updated successful to DONE!`);
        resetUiForTicket();
        applyTicketDataLocal(response.data);
        onTicketPatched?.(response.data);
        await onRefreshUrgents();
      }
    } catch (error) {
      console.error("Error during approval:", error?.response?.data || error?.message);
    } finally {
      setShowDoneConfirmation(false);
      setShowNotification(true);
    }
  };

  const formatEmailDomain = (email) => {
    if (!email) return "";
    const parts = email.split("@");
    if (parts.length < 2) return email;

    const domain = (parts[1] || "").toLowerCase();

    // DB-driven lookup
    return companyDomainMap[domain] || domain;
  };


  const toggleEdit = () => setEditSOBATADATE(false);
  const toggleEditAssignTo = () => setEditAssignTo(false);
  const toggleEditSysFreight = () => setEditSysFreight(false);

  const deleteFileHandler = (file, type) => {
    apiRef.current.put(`/files/remove/${file.fileId}`)
      .then((response) => {
        if (response.status === 200) {
          if (type === "TICKET") {
            removeFile(file.name);
          }
          else if (type === "QUOTATION") {
            removeQuotationFile(file.name);
          }
          else if (type === "FILE_EVIDENCE") {
            setEvidenceFiles(prev => prev.filter(f => f.fileId !== file.fileId));
            setNotificationMessage("Evidence file deleted successfully");
            setShowNotification(true);
          }

        } else {
          setErrorMessage(`Failed to remove file (status: ${response.status}). Please contact IT support!`);
          setShowModal(true);
        }
      })
      .catch((error) => {
        const errorMsg = error?.response?.data || error.message;
        setErrorMessage(`Error removing file: ${errorMsg}. Please contact IT support!`);
        setShowModal(true);
      });
  };

  const handleOpenModal = (type) => {
    if (urgentLock && (type === "ERROR" || type === "REWORK")) {
      setNotificationMessage("[ERROR] Cannot raise " + type + " while ticket is URGENT.");
      setShowNotification(true);
      return;
    }

    if (type === "CANCELLED") {
      setShowCancelConfirmation(true);
    } else {
      setActionType(type);
      setModalTitle(type === "ERROR" ? "Enter reason for raising an ERROR:" : "Enter reason for raising a REWORK:");
      setModalLabels(type);
      setModalOpen(true);
    }
  };

  const handleCancelTicket = async () => {
    try {
      const response = await apiRef.current.patch(`/tickets/CANCELLED/${currentTicketId}`);
      if (response.status === 200) {
        resetUiForTicket();
        applyTicketDataLocal(response.data);
        onTicketPatched?.(response.data);
        setShowCancelConfirmation(false);
        setNotificationMessage(`Ticket ${currentTicketId} updated successful to CANCELLED!`);
        setShowNotification(true);
      }
    } catch (error) {
      console.error("Error during approval:", error);
    }
  };

  const handleCloseRaisErrorReworkModal = () => setModalOpen(false);

  const handleSubmitReason = async (input, errorStage) => {
    try {
      await apiRef.current.patch(`/tickets/update-ticket/error-rework/${currentTicketId}`, null, {
        params: { status: actionType, reason: input, stage: errorStage }
      });
      onSidePanelsInvalidated();
    } catch (error) {
      handleApiError(error, "Error completing action. Please contact IT support.");
    } finally {
      setModalOpen(false);
      setNotificationMessage(`Ticket ${currentTicketId} have been updated to ${modalLabels}!`);
      setShowNotification(true);
    }
  };

  function getFilteredStageListFromDefaults(defaultStagesArg, excludeAfter, includeTarget = false) {
    const list = (defaultStagesArg || [])
      .filter(n => {
        if (n === "All Tickets" || n === "Ticket Closed" || n === "Booking Creation") return false;
        return true;
      });
    const idx = list.findIndex(n => n === excludeAfter);
    return idx !== -1 ? list.slice(0, includeTarget ? idx + 1 : idx) : list;
  }

  const handleCancelUpdateSysFreightNumber = () => {
    setEditSysFreight(true);
    setErrors(prev => ({ ...prev, SysFreight: "" }));
  };

  const getSystemTimeZone = () => new Intl.DateTimeFormat().resolvedOptions().timeZone;
  const systemTimeZone = getSystemTimeZone();

  const startOfDayUTCInTZ = (date, timeZone) => {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone, year: "numeric", month: "2-digit", day: "2-digit",
    }).formatToParts(date).reduce((acc, p) => (p.type !== "literal" ? { ...acc, [p.type]: p.value } : acc), {});
    return Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day));
  };

  const isWithinReworkErrorWindow = React.useCallback((completedAt, days = 30) => {
    if (!completedAt) return false;
    const completed = new Date(completedAt);
    if (Number.isNaN(+completed)) return false;
    const msPerDay = 24 * 60 * 60 * 1000;
    const completedMidnightUTC = startOfDayUTCInTZ(completed, systemTimeZone);
    const todayMidnightUTC = startOfDayUTCInTZ(new Date(), systemTimeZone);
    const daysOld = Math.floor((todayMidnightUTC - completedMidnightUTC) / msPerDay);
    return daysOld < days;
  }, [systemTimeZone]);

  const raiseRework = React.useCallback((status, completedAt) => {
    if (['ADMIN'].some(role => userRoles.includes(role))) {
      return true;
    }
    if (['ERROR', 'REWORK', 'NEW', 'CANCELLED'].includes(status)) return false;
    if (status === 'DONE') {
      return isWithinReworkErrorWindow(completedAt, 30);
    }
    return true;
  }, [stages, userRoles, isWithinReworkErrorWindow]);

  const filteredStages = useMemo(() => {
    if (!Array.isArray(defaultStages)) return [];
    return defaultStages.filter(s => {
      if (s.name === "Ticket Closed") return false;
      if (s.name === "Booking Creation" || s.name === "Booking Creation In Sysfreight") {
        const sysRef = sysFreightRef?.trim();
        const hasBooking = bookingRef != null && bookingRef.trim() !== "";
        const showBookingCreation = (!sysRef || sysRef.toLowerCase() === "tba") && hasBooking;
        return showBookingCreation;
      }
      return true;
    });
  }, [defaultStages, sysFreightRef, bookingRef]);

  const handleSubmitBackupUser = async (backupEmail) => {
    try {
      const res = await apiRef.current.put(
        `/tickets/ticket/backup/${currentTicketId}`,
        null,
        { params: { backup: backupEmail } }
      );

      if (res.status === 200) {
        // update local ticket state so UI reflects backup
        applyTicketDataLocal(res.data);
        onTicketPatched?.(res.data);

        setNotificationMessage("Backup user updated successfully!");
        setShowNotification(true);
      }
    } catch (err) {
      handleApiError(err, "Failed to assign backup user.");
    } finally {
      setBackupButton(false);
    }
  };

  const handleSubmitFileEvidence = async ({ value, files }) => {
    try {
      const n = Number(value);
      if (!Number.isInteger(n) || n <= 0) {
        setNotificationMessage("[ERROR] Please enter a valid file number.");
        setShowNotification(true);
        return;
      }

      const form = new FormData();
      form.append("fileNumber", String(n));
      (files || []).forEach((f) => form.append("files", f)); // ✅ backend expects "files"

      const res = await apiRef.current.put(
        `/tickets/ticket/fileEvidence/${currentTicketId}`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      if (res.status === 200) {
        applyTicketDataLocal(res.data);
        onTicketPatched?.(res.data);

        setNotificationMessage("File evidence updated successfully!");
        setShowNotification(true);
      }
    } catch (err) {
      handleApiError(err, "Failed to update evidence file.");
    } finally {
      setShowFileNumberModal(false);
    }
  };

  const formatDateTime = (v) => {
    if (!v) return "-";

    // Ensure UTC by appending Z if no timezone info is present
    const hasTZ = /[zZ]|[+-]\d{2}:\d{2}$/.test(v);
    const d = new Date(hasTZ ? v : v + "Z");

    if (Number.isNaN(+d)) return String(v);

    return d.toLocaleString(); // converts to user's local timezone
  };

  const urgentExcludedStages = useMemo(
    () => new Set([
      "ETD Port Of Loading",
      "ETA Port Of Arrival",
      "Final ATA Confirmation",
      "Final SOB Confirmation",
      "Ticket Closed",
    ]),
    []
  );

  const urgentSelectableStages = useMemo(() => {
    const flow = (stages || [])
      .filter((s) => typeof s === "string" && s.trim().length > 0);

    const idx = flow.findIndex((s) => s === ticketStage);
    const fromCurrent = idx >= 0 ? flow.slice(idx) : [];

    const requestedStages = new Set(
    (urgentList || [])
      .filter((u) => ["APPROVED", "COMPLETED"].includes(u?.approvalStatus))
      .map((u) => u?.stage)
      .filter(Boolean)
  );

    return fromCurrent.filter(
      (s) => !urgentExcludedStages.has(s) && !requestedStages.has(s)
    );
  }, [stages, ticketStage, urgentExcludedStages, urgentList]);

  const submitUrgentRequest = async (stage) => {
    try {
      const res = await apiRef.current.post("/urgent/request-urgent", null, {
        params: { ticketId: currentTicketId, stage }
      });

      setShowUrgentModal(false);

      if(res.status === 200){
        await onRefreshUrgents();
      }

      setNotificationMessage("URGENT request submitted successfully!");
      setShowNotification(true);
    } catch (err) {
      setNotificationMessage("[ERROR] " + getApiErrorMessage(err, "Failed to submit urgent request."));
      setShowNotification(true);
    }
  };

  const handleUrgentCompleteClick = () => {
    setCompeleteMessage(
      `Are you sure you want to COMPLETE URGENT for stage "${activeUrgent?.stage || "-"}"? This will stop urgent reminders.`
    );
    setShowUrgentCompleteConfirmation(true);
  };

  const confirmCompleteUrgent = async (e) => {
    e?.preventDefault?.();
    try {
      const res = await apiRef.current.put("/urgent/complete", null, {
        params: { requestId: activeUrgent.id }
      });

      if (res.status === 200) {
        await onRefreshUrgents(); // should become null if resolved
        setNotificationMessage("URGENT completed successfully. Reminders stopped.");
        setShowNotification(true);
      }
    } catch (err) {
      setNotificationMessage("[ERROR] " + getApiErrorMessage(err, "Failed to complete urgent."));
      setShowNotification(true);
    } finally {
      setShowUrgentCompleteConfirmation(false);
    }
  };

  const openUrgentList = async () => {
    try {
      await onRefreshUrgents();      // ✅ fetch latest
      setShowUrgentDetails(true);
    } catch {
      setNotificationMessage("[ERROR] Failed to load urgent history.");
      setShowNotification(true);
    }
  };

  const canCompleteUrgent = useMemo(() => {
    if (!urgentLock) return false;
    if (!activeUrgent?.stage) return false;

    return true;
  }, [urgentLock, activeUrgent, ticketStage, ticketStatus]);

  const noNextStage = (selectedStageName.findIndex(
      stage => stage === "HBL Creation"
    ) === (selectedStageName.length-1));

    const fetchPreviewBlob = async (file) => {
    const res = await apiRef.current.get("/files/retrieve", {
      params: { fileId: file.fileId },
      responseType: "arraybuffer",
    });

    // If your backend returns content-type headers, use them; else infer by extension
    const contentType =
      res.headers?.["content-type"] ||
      (file.name?.toLowerCase().endsWith(".pdf") ? "application/pdf" : "image/*");

    return new Blob([res.data], { type: contentType });
  };

  if (!isCreate && (
    !ticketInfo || !currentTicketId ||
    (ticketId != null && String(currentTicketId) !== String(ticketId))
  )) {
    return <div>Loading ticket…</div>;
  }
  
  return (
    <div>
      <div className="ticket-form-header-container">
        <h1>{title === "create-ticket" ? "Create Ticket" : `Ticket ID: ${currentTicketId}`}</h1>
        <div>
          {title === "view-ticket" && (["ADMIN","BOMANAGER"].some(r => userRoles.includes(r)) || (["BOUSER"].some(r => userRoles.includes(r)) && (assignTo === userEmail || boBackupEmail === userEmail))) && (
            <button
              type="button"
              className="btn btn-info me-2"
              onClick={() => setShowFileNumberModal(true)}
            >
              Update File Number
            </button>
          )}
          {title === "view-ticket" && (["ADMIN","BOMANAGER", "TEAMLEAD"].some(r => userRoles.includes(r)) || (["BOUSER"].some(r => userRoles.includes(r)) && assignTo === userEmail)) && (
            <button
              type="button"
              className="btn btn-info me-2"
              onClick={() => setBackupButton(true)}
            >
              Assign Backup
            </button>
          )}
          {title === "view-ticket" &&
            ["ADMIN","FOMANAGER","FOUSER"].some(r => userRoles.includes(r)) && (
              <>
                {/* 1️⃣ Request Urgent (ONLY when no active urgent) */}
                {!urgentLock && (
                  <button
                    type="button"
                    className="btn btn-danger m-2"
                    onClick={() => setShowUrgentModal(true)}
                    disabled={
                      !urgentSelectableStages.length ||
                      ["DONE", "CANCELLED"].includes(ticketStatus)
                    }
                    title={
                      !urgentSelectableStages.length
                        ? "No eligible stages for urgent."
                        : ""
                    }
                  >
                    Request Urgent
                  </button>
                )}

                {/* 2️⃣ View Urgent (ONLY when active urgent exists) */}
                {urgentList && (
                  <button
                    type="button"
                    className="btn btn-outline-danger"
                    onClick={openUrgentList}
                    title="View urgent history"
                  >
                    View Urgent
                  </button>
                )}
              </>
          )}

        </div>
      </div>

      <div className="mb-3 row">
        {title === "view-ticket" && (["ADMIN", "FOMANAGER", "FOUSER"].some(r => userRoles.includes(r)) || (["BOUSER"].some(r => userRoles.includes(r)) && activeUrgent?.approvalStatus === "APPROVED")) && (activeUrgent || pendingUrgent) && (
          <div className="col">
            <div className="row">
              <label className="col-sm-4 col-form-label">URGENT Request:</label>

              <div className="col-sm-8">
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control custom-input"
                    readOnly
                    value={
                      urgentLock
                        ? `${activeUrgent?.completedDateTime ? "COMPLETED" : "ACTIVE"} (${activeUrgent?.status || activeUrgent?.approvalStatus || "URGENT"}) - ${activeUrgent?.stage || "-"}`
                        : `${pendingUrgent?.completedDateTime ? "COMPLETED" : "ACTIVE"} (${pendingUrgent?.status || pendingUrgent?.approvalStatus || "URGENT"}) - ${pendingUrgent?.stage || "-"}`
                    }
                  />
                </div>

              </div>
            </div>
          </div>
        )}

        {title === "view-ticket" && ["ADMIN", "BOMANAGER", "BOUSER"].some(r => userRoles.includes(r)) && boBackupEmail && (
        <div className="col">
          <div className="row">
            <label className="col-sm-4 col-form-label">BO Backup User:</label>
            <div className="col-sm-8">
              <div className="input-group">
                <input
                  type="text"
                  className="form-control custom-input"
                  readOnly
                  value={boBackupEmail || "-"}
                />
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => {
                    setShowRemoveBackupUserModal(true);
                  }}
                >
                  Remove Backup
                </button>
              </div>
            </div>
          </div>
        </div>
        )}

        {/* <div className="col">
          <div className="row">
            <label className="col-sm-4 col-form-label">FO Backup User:</label>
            <div className="col-sm-8">
              <input
                type="text"
                className="custom-input"
                readOnly
                value={ticketInfo?.foBackupEmail || "-"}
              />
            </div>
          </div>
        </div> */}

        {(title === "view-ticket" && ["ADMIN", "BOMANAGER", "BOUSER"].some(r => userRoles.includes(r)) && jobFileNumberInput !== 0) && (
          <div className="col">
            <div className="row align-items-center">
              <label className="col-sm-4 col-form-label">
                File Number:
              </label>

              <div className="col-sm-8">
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control custom-input"
                    readOnly
                    value={jobFileNumberInput || "-"}
                  />
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    disabled={!evidenceFiles?.length}
                    onClick={() => {
                      setSelectedEvidenceFile(evidenceFiles[0] || null);
                      setShowEvidenceViewer(true);
                    }}
                  >
                    View Evidence
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <form>
        <div className="mb-3 row align-items-start">
          <div className="col">
            <div className="row">
              <label htmlFor="sysFreightRef" className="col-sm-4 col-form-label">SysFreight Reference Number:</label>
              <div className="col-sm-8">
                <div className="input-icon-group">
                  <input
                    type="text"
                    readOnly={((title === "view-ticket" && editSysFreight && !["BOUSER", "ADMIN"].some(role => userRoles.includes(role))) || (title === "update-ticket" && ["BOUSER"].some(role => userRoles.includes(role))))}
                    className={`custom-input ${errors.SysFreight ? "is-invalid" : ""}`}
                    id="sysFreightRef"
                    value={sysFreightRef || ""}
                    onChange={(e) => setSysFreightRef(e.target.value)}
                    maxLength={255}
                  />
                  {errors.SysFreight && <div className="invalid-feedback">{errors.SysFreight}</div>}
                  {editSysFreight && (title !== "update-ticket" && title !== "create-ticket") && (!ticketStage.includes("Booking")) &&
                    ["BOUSER", "ADMIN"].some(role => userRoles.includes(role)) && (
                      <FontAwesomeIcon icon={faPen} onClick={toggleEditSysFreight} style={{ cursor: "pointer" }} />
                    )}
                </div>
              </div>
              {(title === "view-ticket" && !editSysFreight) &&
                <div className="sobAtaEditButtonGroup">
                  <button type="button" onClick={handleUpdateTrigger}>Update SysFreight Number</button>
                  <button type="button" onClick={handleCancelUpdateSysFreightNumber}>Cancel</button>
                </div>
              }
            </div>
          </div>

          <div className="col">
            <div className="row">
              <label htmlFor="bookingRef" className="col-sm-4 col-form-label">Booking Reference Number:</label>
              <div className="col-sm-8">
                <div className="input-icon-group">
                  <input
                    type="text"
                    readOnly={(title === "view-ticket" || (title === "update-ticket" && ["BOUSER"].some(role => userRoles.includes(role))))}
                    className={`custom-input ${errors.bookingRef ? "is-invalid" : ""}`}
                    id="bookingRef"
                    value={bookingRef || ""}
                    onChange={(e) => setBookingRef(e.target.value)}
                    maxLength={255}
                  />
                  {errors.bookingRef && <div className="invalid-feedback">{errors.bookingRef}</div>}
                </div>
              </div>
            </div>
          </div>

          {title !== "create-ticket" && stages.some(stage => ["ETD Port Of Loading", "ETA Port Of Arrival", "Final SOB Confirmation", "Final ATA Confirmation"].includes(stage)) &&
            <div className="col">
              <div className="row">
                {(() => {
                  const preferredStages = ["ETD Port Of Loading", "ETA Port Of Arrival"];
                  let matchedStage;

                  if ((currentStageIndex < SOBATAStageIndex || (currentStageIndex === SOBATAStageIndex && !sobAtaDate))|| !stages.some(stage => ["Final SOB Confirmation", "Final ATA Confirmation"].includes(stage))) {
                    matchedStage = stages.find(stage => preferredStages.includes(stage));
                  } else {
                    matchedStage = stages.find(stage => ["Final SOB Confirmation", "Final ATA Confirmation"].includes(stage));
                  }

                  return matchedStage ? (
                    <label htmlFor="etdEtaDate" className="col-sm-4 col-form-label">
                      {matchedStage}:
                    </label>
                  ) : null;
                })()}
                <div className="col-sm-8 col-form-label">
                  <div className="input-icon-group">
                    <input
                      type={(title === "view-ticket" && editSOBATADATE) ? "text" : "date"}
                      readOnly={((title === "view-ticket" && editSOBATADATE) || (title === "update-ticket" && !["BOUSER", "ADMIN"].some(role => userRoles.includes(role))))}
                      className={(title === "view-ticket" && editSOBATADATE)
                        ? "custom-input"
                        : `custom-input ${errors.etdEtaDate ? "is-invalid" : ""}`}
                      id="etdEtaDate"
                      value={((currentStageIndex < SOBATAStageIndex || (currentStageIndex === SOBATAStageIndex && !sobAtaDate)) || !stages.some(stage => ["Final SOB Confirmation", "Final ATA Confirmation"].includes(stage))) ? (etdEtaDate || "") : (sobAtaDate || "")}
                      onChange={(e) => ((currentStageIndex < SOBATAStageIndex || (currentStageIndex === SOBATAStageIndex && !sobAtaDate)) || SOBATAStageIndex === -1) ? setETDETADate(e.target.value) : setSOBATADate(e.target.value)}
                    />
                    {errors.etdEtaDate && <div className="invalid-feedback">{errors.etdEtaDate}</div>}

                    {editSOBATADATE && (title !== "update-ticket" && title !== "create-ticket") &&
                      ["BOUSER", "ADMIN"].some(role => userRoles.includes(role)) &&
                      ((etdEtaDate || sobAtaDate) && !["Booking Creation", "ETD Port Of Loading", "ETA Port Of Arrival"].includes(ticketStage) || (ticketStatus !== "DONE" && ["Ticket Closed"].includes(ticketStage)) ) && (
                        <FontAwesomeIcon icon={faPen} onClick={toggleEdit} style={{ cursor: "pointer" }} />
                      )}
                  </div>
                  {(title === "view-ticket" && !editSOBATADATE) &&
                    <div className="sobAtaEditButtonGroup">
                      <button
                        type="button"
                        onClick={handleUpdateTrigger}
                      >
                        Update {((currentStageIndex < SOBATAStageIndex || (currentStageIndex === SOBATAStageIndex && !sobAtaDate)) || SOBATAStageIndex === -1)
                          ? (stages.includes("ETD Port Of Loading") ? "ETD Port Of Loading Date" : "ETA Port Of Arrival Date")
                          : (stages.includes("Final SOB Confirmation") ? "Final SOB Confirmation" : "Final ATA Confirmation")}
                      </button>
                      <button type="button" onClick={() => setEditSOBATADATE(true)}>Cancel</button>
                    </div>
                  }
                </div>
              </div>
            </div>
          }
        </div>

        {title !== "create-ticket" &&
          <div className="mb-3 row align-items-start">
            <div className="col">
              <div className="row">
                <label htmlFor="ticket-from" className="col-sm-4 col-form-label">Ticket From:</label>
                <div className="col-sm-8">
                  <input type="text" readOnly className="custom-input" id="ticket-from" value={ticketFrom || ""} />
                </div>
              </div>
            </div>

            <div className="col">
              <div className="row">
                <label htmlFor="domain" className="col-sm-4 col-form-label">Domain:</label>
                <div className="col-sm-8">
                  <input type="text" readOnly className="custom-input" id="domain" value={formatEmailDomain(ticketFrom) || ""} />
                </div>
              </div>
            </div>

            <div className="col">
              <div className="row">
                <label htmlFor="assign-to" className="col-sm-4 col-form-label">Ticket Assign To:</label>
                <div className="col-sm-8">
                  <div className="input-icon-group">
                    {(title === "view-ticket" && editAssignTo) &&
                      <input type="text" readOnly className="custom-input" id="assign-to" value={assignTo || ""} />}
                    {(((title !== "view-ticket") && ["BOMANAGER", "FOMANAGER", "ADMIN", "TEAMLEAD"].some(role => userRoles.includes(role))) ||
                      (title === "view-ticket" && !editAssignTo)) &&
                      <SearchableDropdown
                        id="assign-to-dd"
                        options={userList.filter(u => !u.on_leave)}
                        selectedValue={assignTo}
                        onSelectedChange={handleSelectionChange}
                        placeholder="Select user"
                      />}
                    {(title === "view-ticket" && editAssignTo) && ["ADMIN", "TEAMLEAD"].some(role => userRoles.includes(role)) &&
                      (title !== "update-ticket" && title !== "create-ticket") &&
                      <FontAwesomeIcon icon={faPen} onClick={toggleEditAssignTo} style={{ cursor: "pointer" }} />}
                  </div>
                  {(title === "view-ticket" && !editAssignTo) &&
                    <div className="sobAtaEditButtonGroup">
                      <button type="button" onClick={handleUpdateTrigger}>Update Assign To</button>
                      <button type="button" onClick={() => setEditAssignTo(true)}>Cancel</button>
                    </div>
                  }
                </div>
              </div>
            </div>
          </div>}

        <div className="mb-3 row align-items-start">
          <div className="col">
            <div className="row">
              <label htmlFor="serviceType" className="col-sm-4 col-form-label">Service Type:</label>
                {(title === "view-ticket") ?
                  <div className="col-sm-8">
                    <input type="text" readOnly className="custom-input" id="serviceType" value={serviceType || ""} />
                  </div>
                  :
                  <select
                    id="serviceType"
                    className={`form-select ${errors.serviceType ? "is-invalid" : ""}`}
                    value={serviceType || ""}
                    onChange={(e) => {
                      e.preventDefault();
                      setServiceType(e.target.value);
                      setDeliveryAgent(null);
                      setTicketStage("");
                    }}
                  >
                    <option value="" disabled>-- Select --</option>
                    {services.map((type) => (
                      !type.includes("All") &&
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                }
                {errors.serviceType && <div className="invalid-feedback">{errors.serviceType}</div>}
            </div>
          </div>

          {(title === "view-ticket") && <div className="col">
            <div className="row">
              <label htmlFor="ticketStage" className="col-sm-4 col-form-label">Ticket Stage:</label>
              <div className="col-sm-8">
                  <input type="text" readOnly className="custom-input" id="ticketStage" value={ticketStage||""} />
              </div>
            </div>
          </div>}

          {(title === "view-ticket") &&
            <div className="col">
              <div className="row">
                <label htmlFor="ticketStatus" className="col-sm-4 col-form-label">Ticket Status:</label>
                <div className="col-sm-8">
                  <input
                    type="text"
                    readOnly
                    className="custom-input"
                    id="ticketStatus"
                    value={ticketStatus || ""}
                    style={{ color: ticketStatus?.includes("ERROR") ? "red" : ticketStatus?.includes("REWORK")? "orange" : "inherit" }}
                  />
                </div>
              </div>
            </div>
          }
        </div>

        <div className="row mb-3">
          {(title !== "view-ticket") &&
          <div className="col">
            <div className="row" style={{paddingRight: "15px"}}>
              <label htmlFor="ticketStage" className="col-sm-4 col-form-label">Ticket Stage:</label>
              <StagePicker
                stages={filteredStages || []}
                selected={selectedStageName}
                onChange={setSelectedStageNames}
                disabled={!serviceType}
                title="Stages to include (ordered by the default flow)"
                serviceType={serviceType}
              />
            </div>
          </div>}
          <div className="col">
            <div className="row" style={{paddingRight: "15px"}}>
              <label className="col-sm-4 col-form-label">Extra Info Section:</label>
              <div className="stagepicker-extra">
                {(serviceType && selectedStageName.includes("HBL Creation")) &&
                  <div>
                    <div className="stagepicker-radio-wrap">
                      <span className="stagepicker-radio-title">Delivery Agent Type</span>
                      <div className={`stagepicker-radio-group ${errors.deliveryAgent ? "is-invalid" : ""}`}>
                        <div className="form-check">
                          <input
                            className={`form-check-input ${errors.remark ? "is-invalid" : ""}`}
                            type="radio"
                            name="deliveryAgentOption"
                            id="radioCo-LoaderAgent"
                            disabled={title === "view-ticket"}
                            checked={deliveryAgent === "Co-Loader Agent"}
                            onChange={() => handleDeliveryAgent("Co-Loader Agent")}
                          />
                          <label className="form-check-label stagepicker-label" htmlFor="radioCo-LoaderAgent">
                            Co-Loader Agent
                          </label>
                        </div>

                        <div className="form-check">
                          <input
                            className={`form-check-input ${errors.remark ? "is-invalid" : ""}`}
                            type="radio"
                            name="deliveryAgentOption"
                            id="radioKASEOwnAgent"
                            disabled={title === "view-ticket"}
                            checked={deliveryAgent === "KASE Own Agent"}
                            onChange={() => handleDeliveryAgent("KASE Own Agent")}
                          />
                          <label className="form-check-label stagepicker-label" htmlFor="radioKASEOwnAgent">
                            KASE Own Agent
                          </label>
                        </div>
                      </div>
                      {errors.deliveryAgent && <div className="invalid-feedback">{errors.deliveryAgent}</div>}
                    </div>
                  </div>
                }
              </div>
            </div>
          </div>
        </div>

        <div className="mb-3 row">
          <label htmlFor="inputRemark" className="col-sm-2 col-form-label">Remark:</label>
          <div className="col-sm-10">
            {title === "view-ticket" && (
              <button type="button" onClick={handleExpandClick} className="btn btn-link p-0">Expand</button>
            )}
            <textarea
              name="remark"
              className={`custom-input ${errors.remark ? "is-invalid" : ""}`}
              id="inputRemark"
              rows="3"
              value={remark || ""}
              onChange={handleTextAreaChange}
              disabled={title === "view-ticket"}
            />
            {errors.remark && <div className="invalid-feedback">{errors.remark}</div>}
          </div>
        </div>

        <div className="mb-3 row">
          <label htmlFor="quotationText" className="col-sm-2 col-form-label">Quotation:</label>
          <div className="col-sm-10" id="file">
            {title !== "view-ticket" &&
              <div className="switch-container">
                <input type="checkbox" id="toggle" className="switch-checkbox" checked={isChecked} onChange={handleToggleChange} />
                <label className="switch-label" htmlFor="toggle">
                  <span className="switch-option">Text</span>
                  <span className="switch-option">File</span>
                </label>
              </div>}
            {isChecked &&
              <div className={`${errors.quotation ? "is-invalid" : ""}`}>
                <FileList files={quotationFile} removeFile={deleteFileHandler} isUploading={fileUploading} folderName={ticketFolderName} />
                {quotationFile.length < 1 && title !== "view-ticket" && ["BOMANAGER", "FOMANAGER", "ADMIN", "FOUSER"].some(role => userRoles.includes(role)) &&
                  <FileUpload
                    ticketId={ticketId}
                    setFileUploading={setFileUploading}
                    files={quotationFile}
                    setFiles={setQuotationFile}
                    ticketFolderName={ticketFolderName}
                    setTicketFolderName={setTicketFolderName}
                    checkFile={files}
                    type="QUOTATION"
                  />}
              </div>}
            {!isChecked &&
              <div>
                {title === "view-ticket" && (
                  <button type="button" onClick={handleQuotationExpandClick} className="btn btn-link p-0">Expand</button>
                )}
                <textarea
                  name="quotationText"
                  className={`custom-input ${errors.quotation ? "is-invalid" : ""}`}
                  id="quotationText"
                  rows="3"
                  placeholder={(title === "view-ticket" && isChecked) ? "No quotation information..." : "Please type in the quotation information..."}
                  value={quotation || ""}
                  onChange={handleQuotationAreaChange}
                  disabled={title === "view-ticket"}
                />
              </div>
            }
            {errors.quotation && <div className="invalid-feedback">{errors.quotation}</div>}
          </div>
        </div>

        <div className="mb-3 row">
          <label htmlFor="file" className="col-sm-2 col-form-label">Files:</label>
          <div className="col-sm-10" id="file">
            {title !== "view-ticket" && ["BOMANAGER", "FOMANAGER", "ADMIN", "FOUSER"].some(role => userRoles.includes(role)) && (
              <div>
                <FileList files={files} removeFile={deleteFileHandler} isUploading={fileUploading} folderName={ticketFolderName} />
                <FileUpload
                  ticketId={ticketId}
                  setFileUploading={setFileUploading}
                  files={files}
                  setFiles={setFiles}
                  ticketFolderName={ticketFolderName}
                  setTicketFolderName={setTicketFolderName}
                  checkFile={quotationFile}
                  type="TICKET"
                />
              </div>
            )}
            {title === "view-ticket" && (() => {
              const mergedFiles = [...(files || []), ...(quotationFile || [])];

              return (
                <>
                  <div className="row">
                    <div className="col-8">
                      <select
                        key={`file-list-${currentTicketId}`}
                        id="fileList"
                        className="form-select"
                        value={fileSelectionKey(selectedFile)}
                        onChange={(e) =>
                          setSelectedFile(mergedFiles.find(f => fileSelectionKey(f) === e.target.value) || null)
                        }
                        disabled={!mergedFiles.length}
                      >
                        {!mergedFiles.length && <option value="">No files attached</option>}
                        {mergedFiles.map(file => (
                          <option key={`${currentTicketId}-${fileSelectionKey(file)}`} value={fileSelectionKey(file)}>
                            {file.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-4">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={downloadAllFiles}
                        disabled={!mergedFiles.length}
                      >
                        Download All Files
                      </button>
                    </div>
                  </div>

                  {selectedFile && (
                    <FileViewer
                      key={`${currentTicketId}-${fileSelectionKey(selectedFile)}`}
                      fileInfo={selectedFile}
                      folderName={ticketFolderName}
                    />
                  )}
                </>
              );
            })()}
          </div>
        </div>

        {(title === "create-ticket") && (
          <div>
            <button type="button" className="btn btn-primary ms-2" onClick={handleSubmit}>CREATE</button>
            <button type="button" className="btn btn-secondary ms-2" onClick={handleCancelButton}>CANCEL</button>
          </div>
        )}

        {(title === "update-ticket") && (
          <div>
            <button type="button" className="btn btn-primary" onClick={handleUpdateTrigger}>UPDATE</button>
            <button type="button" className="btn btn-secondary ms-2" onClick={handleCancelButton}>CANCEL</button>
          </div>
        )}

        {title === "view-ticket" && (
          <div>
            {!noNextStage && ((!["CANCELLED", "NEW", "ERROR", "REWORK", "URGENT"].some(status => ticketStatus?.includes(status)) || (ticketStage === "Booking Creation")) &&
              (ticketStage !== "Ticket Closed") &&
              ["BOUSER", "BOMANAGER", "ADMIN"].some(role => userRoles.includes(role))) && (
                <button type="button" className="btn btn-primary ms-2" onClick={() => handleComplete("COMPLETE")}>
                  {["ETD Port Of Loading", "ETA Port Of Arrival", "Final SOB Confirmation", "Final ATA Confirmation"].includes(ticketStage)
                    ? `Confirm ${ticketStage} To Complete`
                    : ((ticketStage === "HBL Creation" && ticketStatus === "WAITING FOR SI") ? "SI PROVIDED" : "COMPLETE")}
                </button>
              )}
            {!noNextStage && (["NEW", "ERROR", "REWORK", "URGENT"].some(status => ticketStatus?.includes(status)) &&
              ((ticketStage !== "Ticket Closed" && ticketStage !== "Booking Creation") || (ticketStage === "Ticket Closed" && ticketStatus === "NEW")) &&
              ["BOUSER", "BOMANAGER", "ADMIN"].some(role => userRoles.includes(role))) && (
                <button type="button" className="btn btn-primary ms-2" onClick={() => handleComplete("IN PROGRESS")}>
                  {["ETD Port Of Loading", "ETA Port Of Arrival", "Final SOB Confirmation", "Final ATA Confirmation"].includes(ticketStage) ? `Add ${ticketStage}` : "IN PROGRESS"}
                </button>
              )}
            {!noNextStage && ((ticketStatus === "IN PROGRESS" && ticketStage === "Ticket Closed") &&
              ["BOUSER", "BOMANAGER", "ADMIN"].some(role => userRoles.includes(role))) && (
                <button type="button" className="btn btn-primary ms-2" onClick={handleDone}>
                  TICKET DONE
                </button>
              )}

            {(["FOUSER", "FOMANAGER", "BOMANAGER", "ADMIN"].some(role => userRoles?.includes(role)) && !["DONE", "CANCELLED"].includes(ticketStatus)) && (
              <button type="button" className="btn btn-primary ms-2" onClick={() => handleUpdateButton(currentTicketId)}>
                EDIT
              </button>
            )}

            {(["BOUSER", "BOMANAGER", "ADMIN"].some(role => userRoles.includes(role)) &&
              ((currentStageIndex > ETAETDStageIndex) && !etdEtaDate) && 
              ["ETD Port Of Loading", "ETA Port Of Arrival", "Final SOB Confirmation", "Final ATA Confirmation"].some( stage => selectedStageName.includes(stage)) &&
              (ticketStage !== "Ticket Closed")) && (
                <button type="button" className="btn btn-primary ms-2" onClick={() => handleAddSOBDate()}>
                  {currentStageIndex >= SOBATAStageIndex ? "Add SOB/ATA Date" : "Add ETD/ETA Date"}
                </button>
            )}

            {canCompleteUrgent && ["BOUSER", "BOMANAGER", "ADMIN"].some(role => userRoles.includes(role)) && !activeUrgent?.completedDateTime && 
              <button
                type="button"
                className="btn btn-warning ms-2"
                onClick={handleUrgentCompleteClick}
                disabled={!canCompleteUrgent}
                title={
                  canCompleteUrgent
                    ? "Stop urgent reminders"
                    : "To complete URGENT, this ticket stage must be IN PROGRESS first."
                }
              >
                COMPLETE URGENT
              </button>
            }

            {(
              ((["ADMIN"].some(role => userRoles.includes(role))) ||
                (!["BOUSER"].some(role => userRoles.includes(role)) &&
                !["ERROR", "REWORK", "CANCELLED"].includes(ticketStatus) &&
                (ticketStatus !== "DONE" || isWithinReworkErrorWindow(ticketCompletedDate, 30)) &&
                !(["NEW"].includes(ticketStatus) && currentStageIndex <= 1))) && (
                  <button
                    type="button"
                    className="btn btn-danger ms-2"
                    onClick={() => handleOpenModal("ERROR")}
                  >
                    RAISE ERROR
                  </button>
              ))
            }

            {raiseRework(ticketStatus, ticketCompletedDate) && (
              <button type="button" className="btn btn-warning ms-2" onClick={() => handleOpenModal("REWORK")}>
                RAISE REWORK
              </button>
            )}

            {(["FOUSER", "FOMANAGER", "BOMANAGER", "ADMIN"].some(role => userRoles?.includes(role)) && !["DONE","CANCELLED"].includes(ticketStatus)) && (
              <button type="button" className="btn btn-dark ms-2" onClick={() => handleOpenModal("CANCELLED")}>
                CANCEL
              </button>
            )}
            
            <button type="button" className="btn btn-secondary ms-2" onClick={handleBackToHome}>
              BACK TO HOME
            </button>
          </div>
        )}
      </form>

      <DateInputModal
        isOpen={showInputPopup}
        onClose={handleDateModalOnClose}
        labels={dateInputTitle}
        onSubmit={handleUserInputSubmit}
        etdetaDate={etdEtaDate}
        date={(currentStageIndex === SOBATAStageIndex && sobAtaDate)?sobAtaDate : etdEtaDate}
      />

      <NotificationModal show={showNotification} message={notificationMessage} onClose={handleModalClose} />
      <NotificationModal show={onCreating} message={"Creating..."} onClose={null} />

      <ConfirmationModal
        show={showUpdateConfirmation}
        onClose={() => setShowUpdateConfirmation(false)}
        onConfirm={handleUpdate}
        message="Are you sure you want to UPDATE this ticket?"
        title="Confirm Update"
      />
      <ConfirmationModal
        show={showCancelConfirmation}
        onClose={() => setShowCancelConfirmation(false)}
        onConfirm={handleCancelTicket}
        message="Are you sure you want to CANCELLED this ticket?"
        title="Confirm Cancellation"
      />
      <ConfirmationModal
        show={showCompleteConfirmation}
        onClose={() => setShowCompleteConfirmation(false)}
        onConfirm={confirmComplete}
        message={completeMessage}
        title="Confirm Completion"
      />
      <ConfirmationModal
        show={showDoneConfirmation}
        onClose={() => setShowDoneConfirmation(false)}
        onConfirm={handleDoneUpdate}
        message="Are you sure you want to mark this ticket as DONE?"
        title="Confirm DONE"
      />

      <ConfirmationModal
        show={showRemoveBackupUserModal}
        onClose={() => setShowRemoveBackupUserModal(false)}
        onConfirm={handleRemoveBackupUser}
        message="Are you sure you want to remove backup user?"
        title="Confirm DONE"
      />

      <ConfirmationModal
        show={showUrgentCompleteConfirmation}
        onClose={() => setShowUrgentCompleteConfirmation(false)}
        onConfirm={confirmCompleteUrgent}
        message={completeMessage}
        title="Confirm Complete URGENT"
      />

      <FileUploadingModal isUploading={fileUploading} />

      <Modal isOpen={isRemarkModalOpen} onClose={closeRemarkModal}>
        <textarea
          name="remark"
          className="custom-input"
          value={remark || ""}
          readOnly
          style={{
            width: "100%",
            height: "95%",      // ✅ fill modal content height
            resize: "none",
            boxSizing: "border-box",
          }}
        />
      </Modal>

      <Modal isOpen={isQuotationModalOpen} onClose={closeQuotationModal}>
        <textarea
          name="quotation"
          className="custom-input"
          value={quotation || ""}
          readOnly
          style={{
            width: "100%",
            height: "95%",      // ✅ fill modal content height
            resize: "none",
            boxSizing: "border-box",
          }}
        />
      </Modal>

      <ErrorReworkModal
        isOpen={isModalOpen}
        onClose={handleCloseRaisErrorReworkModal}
        onSubmit={handleSubmitReason}
        title={modalTitle}
        labels={modalLabels}
        stageList={getFilteredStageListFromDefaults(selectedStageName, ticketStage, true)}
      />

      <TextInputModal
        isOpen={showSysRefModal}
        onClose={() => setShowSysRefModal(false)}
        onSubmit={handleSubmitSysRef}
        label="SysFreight Reference Number"
        placeholder="Enter SysFreight Ref (e.g. KASE-123456)"
        defaultValue={sysRefInput}
        submitText="Complete Stage"
      />

      <NumberAndAttachmentModal
        isOpen={showFileNumberModal}
        onClose={() => setShowFileNumberModal(false)}
        onSubmit={handleSubmitFileEvidence}
        label="File Number"
        placeholder="Enter File Number"
        defaultValue={jobFileNumberInput}
        submitText="Submit"
        accept=".jpg,.jpeg,.png,.pdf"
        allowMultiple
        requireFile={true}
        existingFiles={evidenceFiles}
        onRemoveExisting={(file) => deleteFileHandler(file, "FILE_EVIDENCE")}
      />

      <TicketAddBackupModal
        isOpen={backupButon}
        onClose={() => setBackupButton(false)}
        onSubmit={handleSubmitBackupUser}
        userList={userList}
        currentUserEmail={ticketFrom}
        currentBackupEmail={boBackupEmail}
      />

      <EvidenceViewerModal
        isOpen={showEvidenceViewer}
        onClose={() => setShowEvidenceViewer(false)}
        evidenceFiles={evidenceFiles}
        selectedFile={selectedEvidenceFile}
        onSelectFile={setSelectedEvidenceFile}
        fetchPreviewBlob={fetchPreviewBlob}
        onDownload={(file) => fetchFile(file.name, file.fileId)}
      />

      <UrgentRequestModal
        isOpen={showUrgentModal}
        onClose={() => setShowUrgentModal(false)}
        onSubmit={submitUrgentRequest}
        stageList={urgentSelectableStages}
      />

      <UrgentDetailsModal
        isOpen={showUrgentDetails}
        onClose={() => setShowUrgentDetails(false)}
        urgentList={urgentList}
        loading={loadingUrgents}
        formatDateTime={formatDateTime}
      />

      <NotificationModal show={showModal} message={errorMessage} onClose={handleCloseModal} />
    </div>
  );
}

export default TicketForm;
